import Keycloak from 'keycloak-js';
import { KeycloakConfig, KeycloakTokenParsed } from './kc-types';
import Vue, {  } from 'vue';
import { IAuth, IUser, IUserGroup, IUserModule } from './types';


// region Состояние
interface IState {
    initialized: boolean;
    onSuccessfulInitAction: (() => void) | null;

    instanceCode: string;

    keycloakToken: string;
    keycloakTokenParsed: KeycloakTokenParsed;
    keycloakIdTokenParsed: KeycloakTokenParsed;

    kotlinAuthReady: boolean;

    user: IUser;
}

const state = Vue.observable<IState>({
    initialized: false,
    onSuccessfulInitAction: null,

    instanceCode: '',

    keycloakToken: '',
    keycloakTokenParsed: (null as unknown as KeycloakTokenParsed),
    keycloakIdTokenParsed: (null as unknown as KeycloakTokenParsed),

    kotlinAuthReady: false,

    user: (null as unknown as IUser),
});
// endregion


// region Утилиты
const authAccess = {
    keycloakDebug: false,
};
(window as unknown as Record<string, unknown>).authAccess = authAccess;

const AUTH_REQUEST_TIMEOUT_MS = 15_000;

class AppLoadError extends Error {
    statusCode?: number;
    errorCode?: string;
    isTimeout?: boolean;

    constructor(message: string, options?: { statusCode?: number; errorCode?: string; isTimeout?: boolean }) {
        super(message);
        this.name = 'AppLoadError';
        this.statusCode = options?.statusCode;
        this.errorCode = options?.errorCode;
        this.isTimeout = options?.isTimeout;
    }
}

const fetchWithTimeout = async (
    input: RequestInfo,
    init?: RequestInit,
    timeoutMs: number = AUTH_REQUEST_TIMEOUT_MS,
): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
        if ((error instanceof DOMException) && (error.name === 'AbortError')) {
            throw new AppLoadError('Authorization request timed out', {
                errorCode: 'TIMEOUT',
                isTimeout: true,
            });
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
};

const buildHttpError = (message: string, response: Response): AppLoadError => new AppLoadError(message, {
    statusCode: response.status,
    errorCode: String(response.status),
});

const buildMaintenanceResponseError = (errorCode: string = '503'): AppLoadError => new AppLoadError(
    'Maintenance page returned instead of API response',
    {
        statusCode: 503,
        errorCode,
    },
);

const isHtmlResponse = (response: Response, responseText: string): boolean => {
    const contentType = (response.headers.get('content-type') ?? '').toLowerCase();
    if (contentType.includes('text/html')) return true;

    const trimmedText = responseText.trim().toLowerCase();
    return trimmedText.startsWith('<!doctype html') || trimmedText.startsWith('<html');
};

const normalizeAppLoadError = (reason: unknown): AppLoadError => {
    if (reason instanceof AppLoadError) return reason;
    if (reason instanceof Error) return new AppLoadError(reason.message);
    return new AppLoadError(String(reason));
};

const isMaintenanceError = (reason: unknown): boolean => {
    const error = normalizeAppLoadError(reason);
    return Boolean(error.isTimeout || ((error.statusCode !== undefined) && (error.statusCode >= 500) && (error.statusCode < 600)));
};

const getMaintenanceErrorCode = (reason: unknown): string => {
    const error = normalizeAppLoadError(reason);
    return error.errorCode ?? (error.isTimeout ? 'TIMEOUT' : 'UNKNOWN');
};

const asyncPingKotlin = async (): Promise<void> => {
    try {
        const response = await fetchWithTimeout(
            '/api/keycloak/ping',
            {
                headers: {
                    'Authorization': `Bearer ${keycloak.token}`
                },
            },
        );
        if (!response.ok) {
            // noinspection ExceptionCaughtLocallyJS
            throw buildHttpError(`Cannot initialize http session in server - response has status ${response.status} ${response.statusText}`, response);
        }
        state.kotlinAuthReady = true;
    } catch (e) {
        state.kotlinAuthReady = false;
        throw e;
    }
};

const KOTLIN_PING_DELAY__AFTER_SUCCESSFUL_UPDATE = 60_000;
const KOTLIN_PING_DELAY__AFTER_FAILED_UPDATE = 10_000;
let nextKotlinPingTime: number = 0;
const pingKotlin = () => {
    if (nextKotlinPingTime > Date.now()) return;
    nextKotlinPingTime = Number.MAX_SAFE_INTEGER;

    asyncPingKotlin()
        .then(() => {
            nextKotlinPingTime = Date.now() + KOTLIN_PING_DELAY__AFTER_SUCCESSFUL_UPDATE;
        })
        .catch((reason) => {
            nextKotlinPingTime = Date.now() + KOTLIN_PING_DELAY__AFTER_FAILED_UPDATE;
            console.error('Error while updating auth on server', reason);
        });
};

const startKotlinPeriodicalPing = () => { setInterval(pingKotlin, 1_000); };

const fetchJson = async <T>(requestAlias: string, input: RequestInfo, init?: RequestInit): Promise<T> => {
    const response = await fetchWithTimeout(input, init);
    if (!response.ok) {
        console.error('Request failed', response);
        throw buildHttpError(`Request "${requestAlias}" failed - response has status ${response.status} ${response.statusText}`, response);
    }

    const responseText = await response.text();
    if (isHtmlResponse(response, responseText)) {
        throw buildMaintenanceResponseError();
    }

    try {
        return (JSON.parse(responseText) as T);
    } catch (error) {
        if (error instanceof SyntaxError) {
            throw new AppLoadError(error.message);
        }
        throw error;
    }
};
// endregion


// region Элементы DOM для индикации загрузки (отображается до запуска приложения Vue)
interface IInitialDomChange {
    spinnerVisible?: boolean;
    error?: boolean;
    title?: string | null;
    message?: string | null;
    html?: string | null;
}

interface IInitialDom {
    container: HTMLDivElement;
    spinner: HTMLDivElement;
    title: HTMLDivElement;
    message: HTMLDivElement;

    init(): void;
    update(change: IInitialDomChange): void;
}

let appAwaitTimer: number | undefined;

const waitForAppStarted = (): void => {
    const appDiv = document.querySelector('#app');
    if (!(appDiv instanceof HTMLElement)) return;

    if (appDiv.childElementCount > 0) {
        removeInitialDom();
        clearInterval(appAwaitTimer);
        appAwaitTimer = undefined;
    }
};

let initialDom: IInitialDom | null = ((): IInitialDom => {
    return {
        container: document.createElement('div'),
        spinner: document.createElement('div'),
        title: document.createElement('div'),
        message: document.createElement('div'),

        init() {
            this.container.style.marginTop = '48px';
            this.container.style.marginBottom = '8px';
            this.container.style.textAlign = 'center';

            this.spinner.style.border = '16px solid #f3f3f3';
            this.spinner.style.borderTop = '16px solid #3498db';
            this.spinner.style.borderRadius = '50%';
            this.spinner.style.width = '120px';
            this.spinner.style.height = '120px';
            this.spinner.style.margin = 'auto';
            this.container.appendChild(this.spinner);

            this.title.style.marginTop = '12px';
            this.title.style.marginBottom = '8px';
            this.title.style.textAlign = 'center';
            this.title.style.fontWeight = '900';
            this.container.appendChild(this.title);

            this.message.style.textAlign = 'center';
            this.container.appendChild(this.message);

            document.body.appendChild(this.container);

            this.update({ title: 'Authorization...' });

            // Запуск анимации у initialDom.spinner
            this.spinner.animate(
                [
                    { transform: 'rotate(0)' },
                    { transform: 'rotate(360deg)' },
                ],
                { easing: 'linear', duration: 2_000, iterations: Number.POSITIVE_INFINITY },
            );

            // Ожидание заполнения элемента #app дочерними элементами
            appAwaitTimer = setInterval(waitForAppStarted, 25);
        },

        update(change: IInitialDomChange) {
            if (change.spinnerVisible !== undefined) {
                this.spinner.style.display = (change.spinnerVisible ? '' : 'none');
            }
            if (change.error !== undefined) {
                const color = (change.error ? 'red' : '');
                this.title.style.color = color;
                this.message.style.color = color;
            }
            if (change.title !== undefined) this.title.textContent = change.title;
            if (change.message !== undefined) this.message.textContent = change.message;
            if (change.html !== undefined) {
                this.title.innerHTML = '';
                this.message.innerHTML = change.html ?? '';
            }
        },
    };
})();

const createContactRowHtml = (iconHtml: string, text: string): string => `
    <div style="display:inline-flex;align-items:center;gap:8px;">
        <span style="width:20px;min-width:20px;color:#7c9db3;font-size:16px;line-height:20px;display:inline-flex;align-items:center;justify-content:center;">${iconHtml}</span>
        <span>${text}</span>
    </div>
`;

const buildMaintenanceHtml = (errorCode: string): string => `
    <div style="min-height:100vh;margin:0;background:#0e324d;display:flex;align-items:center;justify-content:center;padding:20px 16px;font-family:'Segoe UI',Arial,sans-serif;color:#26354d;">
        <div style="width:100%;max-width:860px;background:#ffffff;border-radius:12px;box-shadow:0 8px 30px rgba(7, 26, 44, 0.18);overflow:hidden;">
            <div style="background:#173b59;padding:22px 20px 16px;text-align:center;">
                <img src="/img/logo-full.svg" alt="eAkimat365" style="height:40px;max-width:100%;object-fit:contain;">
            </div>
            <div style="height:4px;background:#abd34f;"></div>
            <div style="padding:20px 20px 18px;">
                <div style="display:flex;align-items:center;justify-content:center;gap:18px;flex-wrap:wrap;margin-bottom:24px;color:#7c9db3;">
                    <span style="font-size:17px;font-weight:600;">Қате коды</span>
                    <span style="font-size:50px;line-height:1;font-weight:400;color:#24344d;">${errorCode}</span>
                    <span style="font-size:17px;font-weight:600;">Код ошибки</span>
                </div>
                <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px;margin-bottom:24px;">
                    <section style="border:1px solid #d7e0ea;border-radius:12px;padding:22px 20px;text-align:center;min-height:194px;display:flex;flex-direction:column;justify-content:flex-start;">
                        <h2 style="margin:0 0 20px;font-size:18px;font-weight:700;line-height:1.3;color:#24344d;">Техникалық жұмыстар</h2>
                        <p style="margin:0;font-size:16px;font-weight:600;line-height:1.42;color:#7c9db3;">
                            Жүйе уақытша қолжетімсіз.<br><br>
                            Бірнеше минуттан кейін<br>
                            қайта кіріп көріңіз.<br><br>
                            Егер мәселе шешілмесе,<br>
                            қолдау қызметіне хабарласыңыз.
                        </p>
                    </section>
                    <section style="border:1px solid #d7e0ea;border-radius:12px;padding:22px 20px;text-align:center;min-height:194px;display:flex;flex-direction:column;justify-content:flex-start;">
                        <h2 style="margin:0 0 20px;font-size:18px;font-weight:700;line-height:1.3;color:#24344d;">Технические работы</h2>
                        <p style="margin:0;font-size:16px;font-weight:600;line-height:1.42;color:#7c9db3;">
                            Система временно недоступна.<br><br>
                            Пожалуйста, попробуйте войти<br>
                            через несколько минут.<br><br>
                            Если проблема сохраняется,<br>
                            обратитесь в службу поддержки.
                        </p>
                    </section>
                </div>
                <div style="text-align:center;margin-bottom:24px;">
                    <div style="margin-bottom:10px;font-size:15px;font-weight:700;color:#24344d;">Қолдау қызметі&nbsp;&nbsp;|&nbsp;&nbsp;Служба поддержки</div>
                    <div style="display:flex;align-items:center;justify-content:center;gap:14px;flex-wrap:wrap;margin-bottom:8px;font-size:15px;font-weight:600;color:#5283ff;">
                        ${createContactRowHtml('☎', '+7 7172 972 242')}
                        ${createContactRowHtml('☎', '+7 778 021 13 17')}
                        <a
                            href="https://api.whatsapp.com/send?phone=77072275758"
                            target="_blank"
                            rel="noopener noreferrer"
                            style="display:inline-flex;align-items:center;gap:8px;color:#5283ff;text-decoration:none;"
                        >
                            <span style="width:20px;min-width:20px;color:#7c9db3;font-size:16px;line-height:20px;display:inline-flex;align-items:center;justify-content:center;">
                                <span class="mdi mdi-whatsapp" style="color:#22c55e;font-size:20px;"></span>
                            </span>
                            <span>+7 707 227 57 58</span>
                        </a>
                    </div>
                    <div style="display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;font-size:15px;font-weight:600;color:#5283ff;">
                        ${createContactRowHtml('<span class="mdi mdi-email-outline" style="color:#7c9db3;font-size:20px;"></span>', 'help@csi.kz')}
                    </div>
                </div>
                <div style="text-align:center;">
                    <button
                        type="button"
                        onclick="window.location.reload()"
                        style="display:inline-flex;align-items:center;justify-content:center;gap:10px;min-width:320px;max-width:100%;padding:14px 24px;border:none;border-radius:6px;background:#05b44f;color:#ffffff;font-size:17px;font-weight:700;line-height:1;cursor:pointer;box-shadow:none;"
                    >
                        <span class="mdi mdi-refresh" style="font-size:22px;line-height:1;"></span>
                        <span>ҚАЙТА КІРУ&nbsp;&nbsp;|&nbsp;&nbsp;ВОЙТИ ЗАНОВО</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
`;

const showMaintenanceNotification = (errorCode: string): void => {
    if (initialDom !== null) {
        initialDom.container.style.marginTop = '0';
        initialDom.container.style.marginBottom = '0';
        initialDom.container.style.width = '100%';
        initialDom.container.style.position = 'fixed';
        initialDom.container.style.top = '0';
        initialDom.container.style.right = '0';
        initialDom.container.style.bottom = '0';
        initialDom.container.style.left = '0';
        initialDom.container.style.zIndex = '2147483647';
        initialDom.container.style.background = '#0e324d';
        initialDom.container.style.display = 'block';
        initialDom.container.style.padding = '0';
        initialDom.title.style.display = 'none';
        initialDom.title.style.margin = '0';
        initialDom.message.style.display = 'block';
        initialDom.message.style.margin = '0';
        initialDom.message.style.height = '100%';
    }
    document.documentElement.style.margin = '0';
    document.documentElement.style.height = '100%';
    document.documentElement.style.background = '#0e324d';
    document.body.style.margin = '0';
    document.body.style.height = '100%';
    document.body.style.background = '#0e324d';

    initialDom?.update({
        spinnerVisible: false,
        error: false,
        title: '',
        message: '',
        html: buildMaintenanceHtml(errorCode),
    });
};

const removeInitialDom = () => {
    if (initialDom !== null) {
        initialDom.container.remove();
        initialDom = null;
    }
};
// endregion


// region Адаптер keycloak
// noinspection SpellCheckingInspection
let keycloak = new Keycloak({
    url: '',
    realm: 'pavlodar-sic',
    clientId: 'pavlodar-ea',
});

function buildCleanRedirectUri(): string {
  const u = new URL(window.location.href);
  return u.origin + u.pathname + u.hash;
}

const keycloakInit = async (): Promise<unknown> =>
  new Promise<void>((resolve, reject) => {
    const redirectUri = buildCleanRedirectUri();
    keycloak
      .init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        flow: 'standard',
        pkceMethod: 'S256',
        responseMode: 'query',
        redirectUri,
      })
      .then(() => {
        const clean = buildCleanRedirectUri();
        if (clean !== window.location.href) {
          history.replaceState(null, '', clean);
        }

        if (!updateTokensFromKeycloak()) throw new Error('Cannot get keycloak auth data');
        resolve();
      })
      .catch(reject);
  });

const updateTokensFromKeycloak = (): boolean => {
    const token = keycloak.token;
    const tokenParsed = keycloak.tokenParsed;
    const idTokenParsed = keycloak.idTokenParsed;
    if ((token === undefined) || (tokenParsed === undefined) || (idTokenParsed === undefined)) return false;

    if (state.keycloakToken !== token) state.keycloakToken = token;
    if (state.keycloakTokenParsed !== tokenParsed) state.keycloakTokenParsed = tokenParsed;
    if (state.keycloakIdTokenParsed !== idTokenParsed) state.keycloakIdTokenParsed = idTokenParsed;

    return true;
};

const startKeycloakTokenCheck = () => {
  keycloak.onTokenExpired = () => {
    keycloak.updateToken(0)
      .then((refreshed) => {
        if (refreshed) {
          updateTokensFromKeycloak();
          pingKotlin();
          if (authAccess.keycloakDebug) {
            console.info("[service/auth]", "Token successfully refreshed");
          }
        } else {
          console.warn("[service/auth]", "Token still valid, no refresh needed");
        }
      })
      .catch(() => {
        console.error("[service/auth]", "Failed to refresh the token, or the session has expired");
        keycloak.login();
      });
  };
};
// endregion


// region Загрузка данных
const asyncLoad = async () => {
    // region Loading instance code and keycloak settings
    initialDom?.update({ message: 'Loading authorization settings...' });

    let instanceCode = '';
    let keycloakConfig: KeycloakConfig = (null as unknown as KeycloakConfig);


    // Loading instance code, keycloak settings
    await Promise.all([
        // Loading instance code
        (async () => {
            const response = await fetchWithTimeout('/api/instance/code');
            if (!response.ok) throw buildHttpError(`Cannot load server instance code - response has status ${response.status} ${response.statusText}`, response);
            const responseText = await response.text();
            if (isHtmlResponse(response, responseText)) throw buildMaintenanceResponseError();
            instanceCode = responseText;
        })(),

        // Loading keycloak settings
        (async () => {
            keycloakConfig = await fetchJson<KeycloakConfig>('Loading instance realm', '/api/instance/realm');
        })(),
    ]);

    state.instanceCode = instanceCode;
    keycloak = new Keycloak(keycloakConfig);
    startKeycloakTokenCheck();
    // endregion


    // region Keycloak initialization (may send browser to login page)
    initialDom?.update({ message: 'Initializing authorization adapter...' });
    await keycloakInit();
    startUserInfoUpdating();
    // endregion


    // region Loading user info, initializing http sessions (now - in kotlin only)
    initialDom?.update({ message: 'Loading user data...' });
    state.user = await asyncLoadUserData();
    await asyncPingKotlin();
    // endregion

    // region Finalization
    nextKotlinPingTime = Date.now() + KOTLIN_PING_DELAY__AFTER_SUCCESSFUL_UPDATE;
    startKotlinPeriodicalPing();
    initialDom?.update({ title: 'Done', message: 'Starting application...' });
    // endregion
    sendHostForTranslate();
};

const asyncLoadUserData = async (): Promise<IUser> => {
    const token = state.keycloakIdTokenParsed;

    const sub = token.sub;
    if (sub === undefined) throw new Error('User ID in keycloak token is not defined');

    let login = '';
    if ('name' in token) login = String(token.name);
    let preferredUsername = '';
    if ('preferred_username' in token) preferredUsername = String(token.preferred_username);
    const roles = (keycloak.realmAccess?.roles ?? []);

    let groups: Array<IUserGroup> = [];
    let modules: Array<IUserModule> = [];

    // Параллельная загрузка групп и модулей
    await Promise.all([
        // Загрузка групп
        (async () => {
            const result = await fetchJson<Array<IUserGroup> | null | undefined>('Loading user groups', `/api-py/get-user-all-groups-level/${token.sub}`);
            groups = (Array.isArray(result) ? result : []);
        })(),

        // Загрузка модулей
        (async () => {
            const result = await fetchJson<Array<IUserModule> | null | undefined>('Loading user modules', `/api-py/user-modules/${token.sub}`)
            modules = (Array.isArray(result) ? result : []);
        })(),
    ]);
    return { login, sub, preferredUsername, roles, groups, modules, modulesLoaded: true };
};

const startLoad = (): void => {
    asyncLoad()
        .then(() => {
            state.initialized = true;
            if (state.onSuccessfulInitAction !== null) state.onSuccessfulInitAction();
        })
        .catch((reason) => {
            console.error('Cannot load auth data', reason);

            if (isMaintenanceError(reason)) {
                showMaintenanceNotification(getMaintenanceErrorCode(reason));
                return;
            }

            initialDom?.update({
                spinnerVisible: false,
                error: true,
                title: 'Cannot load authorization data',
                message: String(reason),
            });
        })
    ;
};

const updateUserInfo = (): void => {
    asyncLoadUserData()
        .then((user) => {
            if (state.user !== user) state.user = user;
        })
        .catch((reason) => {
            console.error('Cannot update user info', reason);
        });
};

const startUserInfoUpdating = (): void => { setInterval(updateUserInfo, 60_000); };
// endregion


const auth: IAuth = {
    get initialized(): boolean { return state.initialized; },
    get instanceCode(): string { return state.instanceCode; },
    get keycloakToken(): string { return state.keycloakToken; },
    get keycloakTokenParsed(): KeycloakTokenParsed { return state.keycloakTokenParsed; },
    get keycloakIdTokenParsed(): KeycloakTokenParsed { return state.keycloakIdTokenParsed; },
    get kotlinAuthReady(): boolean { return state.kotlinAuthReady; },
    get user(): IUser { return state.user; },

    start(next: () => void) {
        if (state.initialized) next();
        else state.onSuccessfulInitAction = next;
    },

    logout() { keycloak.logout(); },

    updatePassword() {
        keycloak.login({
            action: 'UPDATE_PASSWORD',
        });
    },

    removeInitialDom,
};


// region Инициализация
setTimeout(() => {
    // Добавление индикации загрузки
    initialDom?.init();

    // Запуск загрузки данных
    startLoad();
});
// endregion

function sendHostForTranslate() {
    try {
        const params = {
            protocol: window.location.protocol,
            host: window.location.host,
            user_id: state.user.sub
        };
        fetch('/rust-api/init-host', {
            headers: {
                "Content-Type": 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(params)
        });
        fetch('/api-py/init-host', {
            headers: {
                "Content-Type": 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(params)
        });
    } catch (e) {
        return e
    } finally {
    }
}

export default auth;
export { IAuth, IUser, IUserGroup, IUserModule };
